import React from 'react';
import PropTypes from 'prop-types';
import css from './BlogListing.module.css';

import { H4 } from '../Heading/Heading';
import NamedLink from '../NamedLink/NamedLink';
import BlogListSkeleton from '../BlogListSkeleton/BlogListSkeleton';
import { FormattedMessage } from 'react-intl';

const MAX_TITLE_LEN = 60;
const MAX_DESC_LEN = 100;
const MAX_BLOGS = 3;

function truncate(str, n) {
  if (!str) return '';
  return str.length > n ? str.slice(0, n - 1) + '…' : str;
}

const BlogListing = ({ blogs, fetchBlogsInProgress, fetchBlogsError }) => {
  if (fetchBlogsInProgress) {
    return (
      <section className={css.similarItemSection} aria-labelledby="blog-highlights-heading">
        <H4 id="blog-highlights-heading" className={css.itemTitle}>
          <FormattedMessage id="BlogListing.heading" />
        </H4>
        <p className={css.itemSubTitle}>
          <FormattedMessage id="BlogListing.subtitle" />
        </p>
        <BlogListSkeleton count={MAX_BLOGS} />
      </section>
    );
  }
  // Because old listings don't have blogs, we return null if blogs is empty
  // if (fetchBlogsError) {
  //   return (
  //     <div className={css.error}>
  //       <FormattedMessage id="BlogListing.error" defaultMessage="Could not load blog highlights." />
  //     </div>
  //   );
  // }
  if (!blogs || blogs.length === 0) return null;

  const visibleBlogs = blogs.slice(0, MAX_BLOGS);

  const renderBlogCard = blog => {
    const blogId = blog._id || blog.id;
    const listingId = blog.listingId;
    const imageUrl = blog.image?.imageUrl || blog.image?.secure_url;
    const title = truncate(blog.title, MAX_TITLE_LEN);
    const tag = blog.content?.metaTags?.[0] || blog.label || '';
    const desc = truncate(blog.content?.excerpt || blog.content?.description || '', MAX_DESC_LEN);

    let blogCardImgElement = null;
    if (imageUrl) {
      // Parse intrinsic dimensions from the Cloudinary transform segment.
      // Falls back to canonical blog card dimensions when the URL pattern is absent.
      const wMatch = imageUrl.match(/[,/]w_(\d+)/);
      const hMatch = imageUrl.match(/[,/]h_(\d+)/);
      const intrinsicW = wMatch ? parseInt(wMatch[1], 10) : 796;
      const intrinsicH = hMatch ? parseInt(hMatch[1], 10) : 450;

      // Generate a Cloudinary URL at a target width by replacing the w_ param.
      // Drops the h_ param so Cloudinary derives height from the source AR.
      const toCloudinaryBreakpoint = (url, targetW) => {
        if (!url || !url.includes('res.cloudinary.com')) return url;
        return url
          .replace(/([,/])w_\d+/, `$1w_${targetW}`)
          .replace(/[,/]h_\d+/, '');
      };

      const isCloudinary = imageUrl.includes('res.cloudinary.com');
      const srcSet = isCloudinary
        ? `${toCloudinaryBreakpoint(imageUrl, 400)} 400w, ${toCloudinaryBreakpoint(imageUrl, 796)} 796w`
        : undefined;

      blogCardImgElement = (
        <img
          className={css.blogCardImg}
          src={imageUrl}
          srcSet={srcSet}
          sizes={srcSet ? '(max-width: 640px) 400px, 796px' : undefined}
          alt={title || 'Blog post preview'}
          loading="lazy"
          decoding="async"
          width={intrinsicW}
          height={intrinsicH}
        />
      );
    }

    return (
      <NamedLink
        key={blogId}
        name="BlogContentPage"
        params={{ blogId, listingId }}
        className={css.blogLink}
        data-testid="blog-post-card"
      >
        <div className={css.blogCard}>
          {blogCardImgElement}
          <div className={css.blogCardContent}>
            {tag && <div className={css.blogCardTag}>{tag}</div>}
            <div className={css.blogCardTitle}>{title}</div>
            <div className={css.blogCardDesc}>{desc}</div>
          </div>
        </div>
      </NamedLink>
    );
  };

  return (
    <section className={css.similarItemSection} aria-labelledby="blog-highlights-heading">
      <H4 id="blog-highlights-heading" className={css.itemTitle}>
        <FormattedMessage id="BlogListing.heading" />
      </H4>
      <p className={css.itemSubTitle}>
        <FormattedMessage id="BlogListing.subtitle" />
      </p>

      <div className={css.blogItemsGrid}>{visibleBlogs.map(renderBlogCard)}</div>
    </section>
  );
};

BlogListing.propTypes = {
  blogs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      _id: PropTypes.string,
      listingId: PropTypes.string,
      title: PropTypes.string,
      label: PropTypes.string,
      image: PropTypes.shape({
        imageUrl: PropTypes.string,
        secure_url: PropTypes.string,
      }),
      content: PropTypes.shape({
        excerpt: PropTypes.string,
        description: PropTypes.string,
        metaTags: PropTypes.arrayOf(PropTypes.string),
      }),
    })
  ),
  fetchBlogsInProgress: PropTypes.bool,
  fetchBlogsError: PropTypes.bool,
};

export default BlogListing;
